import type { LucideIcon } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

interface ToolbarIconButtonProps {
  icon: LucideIcon;
  onClick?: () => void;
  "aria-label": string;
  className?: string;
  isActive?: boolean;
  disabled?: boolean;
  "aria-pressed"?: boolean;
  tooltipContent?: string;
}

export const ToolbarIconButton = forwardRef<
  HTMLButtonElement,
  ToolbarIconButtonProps
>(
  (
    {
      icon: Icon,
      onClick,
      "aria-label": ariaLabel,
      className,
      isActive = false,
      disabled = false,
      "aria-pressed": ariaPressed,
      tooltipContent,
      ...rest
    },
    ref,
  ) => (
    <button
      {...rest}
      ref={ref}
      data-tooltip-id="tooltip"
      data-tooltip-place="top"
      data-tooltip-content={tooltipContent}
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      disabled={disabled}
      className={cn(
        "shrink-0",
        "flex items-center justify-center p-4 rounded-[12px] transition-all duration-200 hover:bg-background-primary group",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-n-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background-tertiary",
        isActive && "bg-background-CTA",
        disabled && "opacity-50 cursor-not-allowed hover:bg-transparent",
        className,
      )}
    >
      <Icon
        className={cn(
          "w-8 h-8 transition-colors duration-200",
          isActive
            ? "text-white"
            : "text-icons-secondary group-hover:text-white",
          disabled && "text-icons-muted group-hover:text-icons-muted",
        )}
      />
    </button>
  ),
);
